// The Texture Pack "component library". Each entry is a meaningful UI component the player can re-skin:
// clicking it in the editor renders that component IN ISOLATION (ModEditor) and scaffolds its stable
// `tp-*` CLASSES as empty CSS rules. The player writes ANY CSS against those classes — background,
// colour, border, font, padding, anything — so they're not limited to a fixed list of variables.
//
// Layout that's genuinely geometric (the duel-field playmat alignment) stays as numeric VARS, because
// classes can't cleanly express a dozen independent per-zone offsets. Everything else is classes.
//
// To make a NEW component themeable: add a stable `tp-<name>` class to its core elements, add an entry
// here listing those selectors, and add an isolation render case in ModEditor.

export interface Selector {
  /** the stable class, e.g. ".tp-cardinfo-name" */
  sel: string;
  /** one-line plain-English description of what it is */
  explain: string;
}
export interface VarSpec {
  key: string;
  explain: string;
  def: string;
}
export interface Section {
  title: string;
  note?: string;
  scope?: string;
  /** CSS classes to scaffold (the primary surface) */
  selectors?: Selector[];
  /** numeric vars (only the duel-field geometric alignment uses these) */
  vars?: VarSpec[];
}
export interface ThemeComponent {
  id: string;
  label: string;
  blurb: string;
  sections: Section[];
}

export const THEME_COMPONENTS: ThemeComponent[] = [
  {
    id: "global",
    label: "Global / Base",
    blurb: "App-wide hooks. These classes appear on EVERY screen, so styling them themes the whole UI at once.",
    sections: [
      {
        title: "App-wide elements",
        selectors: [
          { sel: ".tp-app", explain: "the whole app background + base font/colour" },
          { sel: ".tp-panel", explain: "every data-window panel body (menus, dialogs, lobby, deck editor)" },
          { sel: ".tp-panel-edge", explain: "the panel's neon edge" },
          { sel: ".tp-btn", explain: "every primary button (the angular neon button)" },
        ],
      },
      {
        title: "Quick global recolour (optional)",
        note: "the app's colour tokens still exist — set them once here to retint everything that uses them",
        vars: [
          { key: "--color-cyan", explain: "primary accent", def: "#19e0ff" },
          { key: "--color-magenta", explain: "highlight / opponent", def: "#ff2d7e" },
          { key: "--color-gold", explain: "currency / attack", def: "#ffc83a" },
          { key: "--color-ink", explain: "text", def: "#eaf6ff" },
        ],
      },
    ],
  },
  {
    id: "duelfield",
    label: "Duel Field",
    blurb: "Align our board onto a custom playmat. One set of numbers covers BOTH sides; sizes keep the card's 59:86 aspect. (Asymmetric playmat? Override one side with --df-z-me-… / --df-z-opp-…)",
    sections: [
      {
        title: "Backgrounds",
        vars: [
          { key: "--df-bg-field", explain: "FULL duel background (transparent = keep the cyber world)", def: "transparent" },
          { key: "--df-field", explain: "the PLAYMAT image: center / contain no-repeat url(assets/mat.png)", def: "none" },
          { key: "--df-zone-dashed", explain: "1 = dashed empty slots so a printed playmat shows through", def: "0" },
        ],
      },
      {
        title: "Cards",
        vars: [
          { key: "--card-scale", explain: "overall card size on the mat", def: "1" },
          { key: "--df-card-x", explain: "nudge every card inside its zone — X", def: "0px" },
          { key: "--df-card-y", explain: "nudge every card inside its zone — Y", def: "0px" },
        ],
      },
      {
        title: "Monster zones",
        vars: [
          { key: "--df-mzone-gap", explain: "gap between the 5 monster zones", def: "6px" },
          { key: "--df-mzone-x", explain: "offset X", def: "0px" },
          { key: "--df-mzone-y", explain: "offset Y", def: "0px" },
          { key: "--df-mzone-size", explain: "zone + card size (keeps aspect)", def: "1" },
        ],
      },
      {
        title: "Spell / Trap zones",
        vars: [
          { key: "--df-szone-gap", explain: "gap between the 5 spell/trap zones", def: "6px" },
          { key: "--df-szone-x", explain: "offset X", def: "0px" },
          { key: "--df-szone-y", explain: "offset Y", def: "0px" },
          { key: "--df-szone-size", explain: "zone + card size (keeps aspect)", def: "1" },
        ],
      },
      {
        title: "Piles — offset X / Y + size",
        note: "field spell · extra deck · graveyard · deck · banish",
        vars: [
          { key: "--df-field-x", explain: "field spell — X", def: "0px" }, { key: "--df-field-y", explain: "field spell — Y", def: "0px" }, { key: "--df-field-size", explain: "field spell — size", def: "1" },
          { key: "--df-extra-x", explain: "extra deck — X", def: "0px" }, { key: "--df-extra-y", explain: "extra deck — Y", def: "0px" }, { key: "--df-extra-size", explain: "extra deck — size", def: "1" },
          { key: "--df-gy-x", explain: "graveyard — X", def: "0px" }, { key: "--df-gy-y", explain: "graveyard — Y", def: "0px" }, { key: "--df-gy-size", explain: "graveyard — size", def: "1" },
          { key: "--df-deck-x", explain: "deck — X", def: "0px" }, { key: "--df-deck-y", explain: "deck — Y", def: "0px" }, { key: "--df-deck-size", explain: "deck — size", def: "1" },
          { key: "--df-banish-x", explain: "banish — X", def: "0px" }, { key: "--df-banish-y", explain: "banish — Y", def: "0px" }, { key: "--df-banish-size", explain: "banish — size", def: "1" }, { key: "--df-banish-rot", explain: "banish — rotation", def: "0deg" },
        ],
      },
      {
        title: "Hands",
        vars: [
          { key: "--df-hand-x", explain: "offset X", def: "0px" }, { key: "--df-hand-y", explain: "offset Y", def: "0px" },
          { key: "--df-hand-gap", explain: "gap between hand cards", def: "6px" }, { key: "--df-hand-size", explain: "hand card size", def: "1" },
        ],
      },
      {
        title: "Stable classes (advanced — write any CSS)",
        selectors: [
          { sel: ".tp-field", explain: "the playmat background layer" },
          { sel: ".tp-mat", explain: "the mat container behind both fields" },
          { sel: ".tp-zone", explain: "an empty zone slot (e.g. opacity:0 to hide)" },
          { sel: ".tp-card", explain: "a card on the mat" },
          { sel: ".tp-hand", explain: "a hand of cards" },
        ],
      },
    ],
  },
  {
    id: "cardinfo",
    label: "Card Info",
    blurb: "The left rail — the hovered-card inspector AND the comms / chat panel below it.",
    sections: [
      {
        title: "Inspector rail",
        selectors: [
          { sel: ".tp-cardinfo", explain: "the rail panel" },
          { sel: ".tp-cardinfo-art", explain: "the card image frame" },
          { sel: ".tp-cardinfo-name", explain: "the card name" },
          { sel: ".tp-cardinfo-type", explain: "the attribute / type line" },
          { sel: ".tp-cardinfo-stats", explain: "the ATK / DEF row" },
          { sel: ".tp-cardinfo-desc", explain: "the description text" },
        ],
      },
      {
        title: "Chat / comms",
        selectors: [
          { sel: ".tp-chat", explain: "the comms panel" },
          { sel: ".tp-chat-header", explain: "the header strip (Comms + tabs)" },
          { sel: ".tp-chat-tab", explain: "the Chat / Log tab buttons" },
          { sel: ".tp-chat-feed", explain: "the message feed well" },
          { sel: ".tp-chat-msg", explain: "a chat message line" },
          { sel: ".tp-chat-input", explain: "the message input box" },
        ],
      },
    ],
  },
  {
    id: "npc",
    label: "NPC Dialog",
    blurb: "The visual-novel conversation box when you talk to an overworld character.",
    sections: [
      {
        title: "Elements",
        selectors: [
          { sel: ".tp-npc", explain: "the whole dialog scene" },
          { sel: ".tp-npc-scrim", explain: "the darkened backdrop behind the character" },
          { sel: ".tp-npc-name", explain: "the nameplate" },
          { sel: ".tp-npc-panel", explain: "the dialog panel" },
          { sel: ".tp-npc-line", explain: "the spoken line" },
        ],
      },
    ],
  },
  {
    id: "overworld",
    label: "Overworld Menu",
    blurb: "The in-world menu (Ranked / Unranked duel, deck, avatar, settings, log out).",
    sections: [
      {
        title: "Elements",
        selectors: [
          { sel: ".tp-worldmenu", explain: "the menu panel" },
          { sel: ".tp-worldmenu-scrim", explain: "the dim backdrop behind it" },
          { sel: ".tp-worldmenu-header", explain: "the header bar" },
          { sel: ".tp-worldmenu-item", explain: "a menu item row" },
          { sel: ".tp-worldmenu-icon", explain: "a menu item's icon tile" },
        ],
      },
    ],
  },
  {
    id: "overlay",
    label: "Overworld Overlay",
    blurb: "The in-world HUD that overlaps the map — unit frame, area pill, players-online and the Map / Shop / Menu buttons.",
    sections: [
      {
        title: "Elements",
        selectors: [
          { sel: ".tp-hud-frame", explain: "the top-left duelist unit frame" },
          { sel: ".tp-hud-area", explain: "the centre area pill" },
          { sel: ".tp-hud-btn", explain: "the players-online / Map / Shop / Menu buttons" },
        ],
      },
    ],
  },
  {
    id: "login",
    label: "Login UI",
    blurb: "The sign-in / title screen.",
    sections: [
      {
        title: "Elements",
        selectors: [
          { sel: ".tp-login", explain: "the whole login screen (set a background here)" },
          { sel: ".tp-login-title", explain: "the 'Evolution' wordmark" },
          { sel: ".tp-login-panel", explain: "the terminal panel" },
          { sel: ".tp-login-header", explain: "the terminal title bar" },
          { sel: ".tp-login-footer", explain: "the footer microline" },
        ],
      },
    ],
  },
  {
    id: "deck",
    label: "Deck Editor",
    blurb: "The deck builder. Built from the shared .tp-panel and .tp-btn classes (style those, or use Custom CSS for finer control).",
    sections: [],
  },
  {
    id: "freeduel",
    label: "Free Duel Lobby",
    blurb: "The Free Duel lobby + queue. Built from the shared .tp-panel and .tp-btn classes (style those, or use Custom CSS for finer control).",
    sections: [],
  },
];

export const componentById = (id: string): ThemeComponent | undefined => THEME_COMPONENTS.find((c) => c.id === id);

/** Generate a component's prefilled CSS scaffold: empty rules for each class (the player fills them
 *  with any CSS), plus a `:root {}` block for any numeric vars. */
export function blockText(c: ThemeComponent): string {
  let out = `/* ════ ${c.label} ════\n   ${c.blurb} */\n`;
  if (!c.sections.length) return out;
  for (const s of c.sections) {
    out += `\n/* ── ${s.title}${s.note ? " — " + s.note : ""} ── */\n`;
    for (const sel of s.selectors ?? []) out += `${sel.sel} {\n  /* ${sel.explain} */\n}\n`;
    if (s.vars?.length) {
      const open = s.scope ?? ":root";
      out += `${open} {\n` + s.vars.map((v) => `  ${v.key}: ${v.def};${v.explain ? `  /* ${v.explain} */` : ""}`).join("\n") + "\n}\n";
    }
  }
  return out;
}
